<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useFollowUpStatus } from 'dashboard/composables/useFollowUpStatus';
import { toquesDoFollowUp } from './toquesDoFollowUp';

// Selo no header da conversa — pedido do Victor (2026-07-22): "consigo ver
// que essa conversa está em follow-up sem abrir o painel separado?". Só
// renderiza quando existe run non_terminal pra essa conversa (ver
// useFollowUpStatus). Clique leva pro painel de gestão completo.
const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const router = useRouter();
const accountId = useMapGetter('getCurrentAccountId');

const { run } = useFollowUpStatus(() => props.conversationId);

// Mensagens enviadas, não o número do passo (ver toquesDoFollowUp).
const toques = computed(() => toquesDoFollowUp(run.value));
const progressPercent = computed(() => toques.value.porcento);

// Achado real (2026-07-22): pause_manually! não limpa next_action_at — sem
// esse estado visual, o selo continuava com a cor "ativo" mesmo pausado,
// dando a entender (errado) que ainda ia mandar mensagem.
const isPaused = computed(() => run.value?.status === 'paused_manual');

// 🔴 O SELO MENTIA SOBRE O ESTADO (Victor, 09/09/2026).
//
// Uma cadência marcada para uma data futura aparecia igualzinha a uma que
// está mandando mensagem hoje: "Follow-up ▬▭▭ 1/7", com barra de progresso.
// Na conversa 4298 (Bruna Rovetta) a primeira mensagem só sai em 01/02/2027,
// e o selo não dizia nada disso — quem olhava concluía que a cadência estava
// correndo e estranhava a conversa ainda estar na fila da Dra. Jullya.
//
// "Agendado" é do dia seguinte em diante: uma cadência que age hoje mais
// tarde é, para quem olha, uma cadência em andamento.
const dataAgendada = computed(() => {
  const epoch = run.value?.next_action_at;
  if (!epoch || isPaused.value) return null;

  const quando = new Date(epoch * 1000);
  const hoje = new Date();
  const mesmoDia =
    quando.getFullYear() === hoje.getFullYear() &&
    quando.getMonth() === hoje.getMonth() &&
    quando.getDate() === hoje.getDate();

  if (mesmoDia || quando <= hoje) return null;

  return quando.toLocaleDateString('pt-BR');
});

const rotuloTooltip = computed(() =>
  dataAgendada.value
    ? t('FOLLOW_UP_CADENCES.INDICATOR.TOOLTIP_SCHEDULED', {
        date: dataAgendada.value,
      })
    : t('FOLLOW_UP_CADENCES.INDICATOR.TOOLTIP')
);

const goToPanel = () => {
  router.push({
    name: 'follow_up_cadences',
    params: { accountId: accountId.value },
  });
};
</script>

<template>
  <button
    v-if="run"
    v-tooltip="rotuloTooltip"
    class="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-colors flex-shrink-0"
    :class="
      isPaused
        ? 'bg-n-slate-3 dark:bg-n-slate-4 text-n-slate-10 hover:bg-n-slate-4'
        : 'bg-n-brand/10 text-n-blue-11 hover:bg-n-brand/15'
    "
    @click="goToPanel"
  >
    <span class="i-ph-repeat size-3.5 flex-shrink-0" />
    <span>{{ t('FOLLOW_UP_CADENCES.INDICATOR.LABEL') }}</span>
    <span v-if="isPaused">{{
      t('FOLLOW_UP_CADENCES.RUN_STATUS.PAUSED_MANUAL')
    }}</span>
    <span v-else-if="dataAgendada">{{
      t('FOLLOW_UP_CADENCES.INDICATOR.SCHEDULED', { date: dataAgendada })
    }}</span>
    <template v-else>
      <span
        class="w-8 h-1 rounded-full bg-n-brand/20 overflow-hidden hidden sm:block"
      >
        <span
          class="block h-full rounded-full bg-n-brand"
          :style="{ width: `${progressPercent}%` }"
        />
      </span>
      <span class="tabular-nums">
        {{
          t('FOLLOW_UP_CADENCES.INDICATOR.ENVIADAS', {
            current: toques.enviados,
            total: toques.total,
          })
        }}
      </span>
    </template>
  </button>
</template>
